import React, { useState } from "react";
import Button from "../../ReUsableComponents/Button";
import DoneIcon from "@mui/icons-material/Done";
import EditIcon from "@mui/icons-material/EditOutlined";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  SearchArea,
  Title,
  Row,
  Colorbox,
  ColorText,
  IconWrapper,
  SmallColorsContainer,
  SmallColorBox,
  BlueButton,
  ButtonContainer,
  RedButton,
} from "./styled";
import {
  labelCreate,
  labelDelete,
  labelUpdate,
  labelUpdateSelection,
} from "../../../../../Services/cardService";
import { openAlert } from "../../../../../Redux/Slices/alertSlice";

const LabelsPopover = (props) => {
  const { currentPage } = props;
  const dispatch = useDispatch();
  const thisCard = useSelector((state) => state.card);
  const [selectedCard, setSelectedCard] = useState({
    _id: "",
    color: "",
    text: "",
    backColor: "",
  });
  const colors = thisCard.colors;

  const handleCreateClick = async (text, color, backColor) => {
    props.arrowCallback(false);
    props.titleCallback("Labels");
    await labelCreate(
      thisCard.cardId,
      thisCard.listId,
      thisCard.boardId,
      text,
      color,
      backColor,
      dispatch
    );
  };

  const handleSaveClick = async (labelId, text, color, backColor) => {
    props.arrowCallback(false);
    props.titleCallback("Labels");
    await labelUpdate(
      thisCard.cardId,
      thisCard.listId,
      thisCard.boardId,
      labelId,
      { text, color, backColor },
      dispatch
    );
  };

  const handleColorBoxClick = async (labelId, selected) => {
    const label = thisCard.labels.find((l) => l._id === labelId);
    if (!label) return;

    const fallbackNames = [
      "Bug",
      "Feature",
      "Improvement",
      "Review",
      "Urgent",
      "Low Priority",
    ];
    const fallbackText =
      label.text?.trim() ||
      fallbackNames[Math.floor(Math.random() * fallbackNames.length)];

    // If label has no text, update it with fallback text before selection
    if (!label.text || label.text.trim() === "") {
      await labelUpdate(
        thisCard.cardId,
        thisCard.listId,
        thisCard.boardId,
        labelId,
        { text: fallbackText, color: label.color, backColor: label.backColor },
        dispatch
      );
    }

    await labelUpdateSelection(
      thisCard.cardId,
      thisCard.listId,
      thisCard.boardId,
      labelId,
      selected,
      dispatch
    );
  };

  const handleDeleteClick = async (labelId) => {
    props.arrowCallback(false);
    props.titleCallback("Labels");
    await labelDelete(
      thisCard.cardId,
      thisCard.listId,
      thisCard.boardId,
      labelId,
      dispatch
    );
  };

  const LabelComponent = (props) => {
    return (
      <Row>
        <Colorbox
          bg={props.color}
          hbg={props.backColor}
          onClick={() => {
            handleColorBoxClick(props._id, !props.selected);
          }}
        >
          <ColorText>{props.text}</ColorText>
          {props.selected && <DoneIcon fontSize="1rem" />}
        </Colorbox>
        <IconWrapper
          onClick={() => {
            setSelectedCard({
              _id: props._id,
              color: props.color,
              text: props.text,
              backColor: props.backColor,
            });
            props.arrowCallback(true);
            props.titleCallback("Change");
          }}
        >
          <EditIcon color="#091e42" fontSize="1rem" />
        </IconWrapper>
      </Row>
    );
  };

  const mainPage = (
    <Container>
      <SearchArea placeholder="Search labels..." />
      <Title>Labels</Title>
      {thisCard.labels.map((label, index) => {
        const defaultNames = [
          "Bug",
          "Feature",
          "Improvement",
          "Review",
          "Urgent",
          "Low Priority",
        ];
        const fallbackText =
          label.text && label.text.trim() !== ""
            ? label.text
            : defaultNames[index % defaultNames.length] || `Label ${index + 1}`;

        return (
          <LabelComponent
            key={label._id}
            {...label}
            text={fallbackText} // override if missing
            arrowCallback={props.arrowCallback}
            titleCallback={props.titleCallback}
          />
        );
      })}

      <br />
      <Button
        clickCallback={() => {
          props.arrowCallback(true);
          props.titleCallback("Create");
        }}
        title="Create a new label"
      />
    </Container>
  );

  const CreatePage = () => {
    const [createText, setCreateText] = useState("");
    const [createColor, setCreateColor] = useState("#0079bf");
    const [createBackColor, setCreateBackColor] = useState("#055a8c");
    const suggestions = ["Bug", "Feature", "Improvement", "Research", "Review"];

    return (
      <Container>
        <Title>Name</Title>
        <SearchArea
          placeholder="Name..."
          value={createText}
          onChange={(e) => setCreateText(e.target.value)}
        />
        <div
          style={{
            marginTop: "10px",
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          {suggestions.map((name) => (
            <div
              key={name}
              style={{
                backgroundColor: "#f0f0f0",
                padding: "4px 8px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.85rem",
              }}
              onClick={() => setCreateText(name)}
            >
              {name}
            </div>
          ))}
        </div>

        <Title>Select a color</Title>
        <SmallColorsContainer>
          {colors.map((color) => (
            <SmallColorBox
              key={color.bg}
              bg={color.bg}
              hbg={color.hbg}
              onClick={() => {
                setCreateColor(color.bg);
                setCreateBackColor(color.hbg);
              }}
            >
              {color.bg === createColor && <DoneIcon fontSize="1rem" />}
            </SmallColorBox>
          ))}
        </SmallColorsContainer>

        <ButtonContainer>
          <BlueButton
            onClick={() => {
              if (createText && createColor && createBackColor)
                handleCreateClick(createText, createColor, createBackColor);
              else
                dispatch(
                  openAlert({
                    severity: "error",
                    message: "Please fill all required areas!",
                  })
                );
            }}
          >
            Create
          </BlueButton>
        </ButtonContainer>
      </Container>
    );
  };

  const ChangePage = () => {
    const [changeText, setChangeText] = useState(selectedCard.text);
    const [changeColor, setChangeColor] = useState(selectedCard.color);
    const [changeBackColor, setChangeBackColor] = useState(
      selectedCard.backColor
    );

    return (
      <Container>
        <Title>Name</Title>
        <SearchArea
          placeholder="Name..."
          value={changeText}
          onChange={(e) => setChangeText(e.target.value)}
        />
        <Title>Select a color</Title>
        <SmallColorsContainer>
          {colors.map((color) => (
            <SmallColorBox
              key={color.bg}
              bg={color.bg}
              hbg={color.hbg}
              onClick={() => {
                setChangeColor(color.bg);
                setChangeBackColor(color.hbg);
              }}
            >
              {changeColor === color.bg && <DoneIcon fontSize="1rem" />}
            </SmallColorBox>
          ))}
        </SmallColorsContainer>

        <ButtonContainer>
          <BlueButton
            onClick={() => {
              if (changeText && changeColor && changeBackColor)
                handleSaveClick(
                  selectedCard._id,
                  changeText,
                  changeColor,
                  changeBackColor
                );
              else
                dispatch(
                  openAlert({
                    severity: "error",
                    message: "Please fill all required areas!",
                  })
                );
            }}
          >
            Save
          </BlueButton>
          <RedButton onClick={() => handleDeleteClick(selectedCard._id)}>
            Delete
          </RedButton>
        </ButtonContainer>
      </Container>
    );
  };

  return (
    <>
      {currentPage === "Labels" ? (
        mainPage
      ) : currentPage === "Create" ? (
        <CreatePage />
      ) : (
        <ChangePage />
      )}
    </>
  );
};

export default LabelsPopover;
